import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@roger/ui-components';
import { Settings } from 'lucide-react';

export type ReportPopulationControlsProps = {
  includeTestRows: boolean;
  onIncludeTestRowsChange: (value: boolean) => void;
  includeInactiveEpisodes?: boolean;
  onIncludeInactiveEpisodesChange?: (value: boolean) => void;
  showImportedPatients?: boolean;
  includeImportedPatients?: boolean;
  onIncludeImportedPatientsChange?: (value: boolean) => void;
  includeMissedVisits?: boolean;
  onIncludeMissedVisitsChange?: (value: boolean) => void;
  showInvoiceClaims?: boolean;
  includeInvoiceClaims?: boolean;
  onIncludeInvoiceClaimsChange?: (value: boolean) => void;
  description?: string;
};

export function ReportPopulationControls({
  includeTestRows,
  includeInactiveEpisodes,
  includeImportedPatients,
  showImportedPatients = includeImportedPatients !== undefined,
  onIncludeTestRowsChange,
  onIncludeInactiveEpisodesChange,
  onIncludeImportedPatientsChange,
  includeMissedVisits,
  onIncludeMissedVisitsChange,
  showInvoiceClaims = false,
  includeInvoiceClaims,
  onIncludeInvoiceClaimsChange,
  description = 'Choose which rows to include in this report.',
}: ReportPopulationControlsProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          aria-label='Report population settings'
        >
          <Settings className='h-4 w-4' aria-hidden='true' />
          <span className='sr-only'>Report population settings</span>
        </Button>
      </DialogTrigger>
      <DialogContent className='w-[calc(100%-2rem)] sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Report population settings</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className='space-y-3'>
          <label className='flex items-center gap-2 text-sm font-medium'>
            <Checkbox
              checked={includeTestRows}
              onCheckedChange={checked =>
                onIncludeTestRowsChange(checked === true)
              }
            />
            Include test rows
          </label>
          {(includeInactiveEpisodes !== undefined ||
            onIncludeInactiveEpisodesChange !== undefined) && (
            <label className='flex items-center gap-2 text-sm font-medium'>
              <Checkbox
                checked={includeInactiveEpisodes ?? false}
                onCheckedChange={checked =>
                  onIncludeInactiveEpisodesChange?.(checked === true)
                }
              />
              Include inactive episodes
            </label>
          )}
          {showImportedPatients && (
            <label className='flex items-center gap-2 text-sm font-medium'>
              <Checkbox
                checked={includeImportedPatients ?? false}
                onCheckedChange={checked =>
                  onIncludeImportedPatientsChange?.(checked === true)
                }
              />
              Include imported patients
            </label>
          )}
          {includeMissedVisits !== undefined && (
            <label className='flex items-center gap-2 text-sm font-medium'>
              <Checkbox
                checked={includeMissedVisits}
                onCheckedChange={checked =>
                  onIncludeMissedVisitsChange?.(checked === true)
                }
              />
              Include missed visits
            </label>
          )}
          {showInvoiceClaims && (
            <label className='flex items-center gap-2 text-sm font-medium'>
              <Checkbox
                checked={includeInvoiceClaims ?? false}
                onCheckedChange={checked =>
                  onIncludeInvoiceClaimsChange?.(checked === true)
                }
              />
              Include invoice-billed claims
            </label>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
